import type { ProfileLink, RowerProfile } from '../services/users/rowerService';

export type RowerProfileCompletionField = {
  id: string;
  label: string;
  complete: boolean;
};

export type RowerProfileCompletionResult = {
  percent: number;
  isComplete: boolean;
  fields: RowerProfileCompletionField[];
  missingLabels: string[];
};

export type RowerProfileCompletionInput = {
  hasAvatar: boolean;
  first_name: string;
  last_name: string;
  bio: string;
  date_of_birth: string;
  gender: string;
  height_cm: string;
  weight_kg: string;
  club: string;
  erg_2k: string;
  gpa: string;
  sat_score: string;
  act_score: string;
  phone: string;
  based_city: string;
  based_region: string;
  based_country_code: string;
  social_instagram: string;
  social_tiktok: string;
  social_youtube: string;
  profileLinks: Array<{ url: string }>;
  awardCount: number;
};

/** Normalize user input to a canonical https? URL, or null if invalid / empty. */
export function parseProfileHttpUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  try {
    const u = new URL(t);
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.href;
  } catch {
    try {
      return new URL(`https://${t}`).href;
    } catch {
      return null;
    }
  }
  return null;
}

function toDateInputValue(value: string | null | undefined): string {
  if (value == null) return '';
  const t = value.trim();
  if (!t) return '';
  const dateOnly = t.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) return dateOnly[1];
  const parsed = new Date(t);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

export function resolveRowerBirthDateForCompletion(
  rowerProfile: { date_of_birth?: string | null },
  user?: { user_metadata?: Record<string, unknown> } | null
): string {
  const fromRower = toDateInputValue(rowerProfile.date_of_birth);
  if (fromRower) return fromRower;
  const metadataBirthDate = user?.user_metadata?.birth_date;
  return typeof metadataBirthDate === 'string' ? toDateInputValue(metadataBirthDate) : '';
}

export function computeRowerProfileCompletion(
  input: RowerProfileCompletionInput
): RowerProfileCompletionResult {
  const hasLoc = Boolean(
    input.based_city.trim() || input.based_region.trim() || input.based_country_code.trim()
  );
  const hasSocial = Boolean(
    input.social_instagram.trim() || input.social_tiktok.trim() || input.social_youtube.trim()
  );
  const hasProfileLink = input.profileLinks.some((row) => parseProfileHttpUrl(row.url));
  const hasMetrics = Boolean(input.height_cm.trim() && input.weight_kg.trim());

  const fields: RowerProfileCompletionField[] = [
    { id: 'avatar', label: 'Profile photo', complete: input.hasAvatar },
    {
      id: 'name',
      label: 'First and last name',
      complete: Boolean(input.first_name.trim() && input.last_name.trim()),
    },
    { id: 'bio', label: 'Bio', complete: Boolean(input.bio.trim()) },
    { id: 'dob', label: 'Date of birth', complete: Boolean(input.date_of_birth.trim()) },
    { id: 'gender', label: 'Gender', complete: Boolean(input.gender.trim()) },
    { id: 'metrics', label: 'Height and weight', complete: hasMetrics },
    { id: 'club', label: 'Club', complete: Boolean(input.club.trim()) },
    { id: 'erg', label: '2k erg', complete: Boolean(input.erg_2k.trim()) },
    { id: 'gpa', label: 'GPA', complete: Boolean(input.gpa.trim()) },
    { id: 'sat', label: 'SAT score', complete: Boolean(input.sat_score.trim()) },
    { id: 'act', label: 'ACT score', complete: Boolean(input.act_score.trim()) },
    { id: 'phone', label: 'Phone', complete: Boolean(input.phone.trim()) },
    { id: 'location', label: 'Location', complete: hasLoc },
    { id: 'social', label: 'Social link', complete: hasSocial },
    { id: 'profile_link', label: 'Video or profile link', complete: hasProfileLink },
    { id: 'award', label: 'Award or credential', complete: input.awardCount > 0 },
  ];

  const completed = fields.filter((f) => f.complete).length;
  const percent = Math.round((completed / fields.length) * 100);
  const missingLabels = fields.filter((f) => !f.complete).map((f) => f.label);

  return {
    percent,
    isComplete: percent >= 100,
    fields,
    missingLabels,
  };
}

export function rowerProfileToCompletionInput(
  rowerProfile: RowerProfile | null | undefined,
  options?: {
    user?: { user_metadata?: Record<string, unknown> } | null;
    hasAvatarOverride?: boolean;
    awardCountOverride?: number;
  }
): RowerProfileCompletionInput | null {
  if (!rowerProfile) return null;

  const sl = rowerProfile.social_links || {};
  const pl = rowerProfile.profile_links;
  const profileLinks = Array.isArray(pl)
    ? pl.map((item) => ({
        url: String((item as ProfileLink)?.url ?? ''),
      }))
    : [];

  const awardCount =
    options?.awardCountOverride ??
    (Array.isArray(rowerProfile.profile_awards) ? rowerProfile.profile_awards.length : 0);

  return {
    hasAvatar: Boolean(options?.hasAvatarOverride ?? rowerProfile.avatar_url),
    first_name: rowerProfile.first_name || '',
    last_name: rowerProfile.last_name || '',
    bio: rowerProfile.bio || '',
    date_of_birth: resolveRowerBirthDateForCompletion(rowerProfile, options?.user),
    gender: rowerProfile.gender || '',
    height_cm: rowerProfile.height_cm != null ? String(rowerProfile.height_cm) : '',
    weight_kg: rowerProfile.weight_kg != null ? String(rowerProfile.weight_kg) : '',
    club: rowerProfile.club || '',
    erg_2k: rowerProfile.erg_2k_seconds != null && rowerProfile.erg_2k_seconds > 0 ? '1' : '',
    gpa: rowerProfile.gpa != null ? String(rowerProfile.gpa) : '',
    sat_score: rowerProfile.sat_score != null ? String(rowerProfile.sat_score) : '',
    act_score: rowerProfile.act_score != null ? String(rowerProfile.act_score) : '',
    phone: rowerProfile.phone || '',
    based_city: rowerProfile.based_city || '',
    based_region: rowerProfile.based_region || '',
    based_country_code: rowerProfile.based_country_code || '',
    social_instagram: (sl.instagram as string) || '',
    social_tiktok: (sl.tiktok as string) || '',
    social_youtube: (sl.youtube as string) || '',
    profileLinks,
    awardCount,
  };
}

export function completionInputFromSettingsForm(
  profile: Omit<
    RowerProfileCompletionInput,
    'hasAvatar' | 'profileLinks' | 'awardCount' | 'erg_2k'
  > & { erg_2k: string },
  extras: {
    hasAvatar: boolean;
    profileLinks: Array<{ url: string }>;
    awardCount: number;
  }
): RowerProfileCompletionInput {
  return {
    ...profile,
    hasAvatar: extras.hasAvatar,
    profileLinks: extras.profileLinks,
    awardCount: extras.awardCount,
    erg_2k: profile.erg_2k,
  };
}
